import { BadgeElementType } from "sharedTypes/badge"

export const EDITOR_RESOLUTION = 8
export const PREVIEW_RESOLUTION = 4
// Overflow extent for the white mask that dims content outside the canvas
export const OVERFLOW_EXTENT = 4000

// Text alignment options
export const BADGE_HORIZONTAL_ALIGNMENT_VALUES = ["left", "center", "right", "justify"] as const
export const BADGE_HORIZONTAL_ALIGNMENT_LABELS: Record<
  typeof BADGE_HORIZONTAL_ALIGNMENT_VALUES[number],
  string
> = {
  left: "Left",
  center: "Center",
  right: "Right",
  justify: "Justify",
}
export const BADGE_HORIZONTAL_ALIGNMENT_OPTIONS = BADGE_HORIZONTAL_ALIGNMENT_VALUES.map((v) => ({
  label: BADGE_HORIZONTAL_ALIGNMENT_LABELS[v],
  value: v,
}))

export const BADGE_VERTICAL_ALIGNMENT_VALUES = ["top", "center", "bottom"] as const
export const BADGE_VERTICAL_ALIGNMENT_LABELS: Record<
  typeof BADGE_VERTICAL_ALIGNMENT_VALUES[number],
  string
> = {
  top: "Top",
  center: "Center",
  bottom: "Bottom",
}
export const BADGE_VERTICAL_ALIGNMENT_OPTIONS = BADGE_VERTICAL_ALIGNMENT_VALUES.map((v) => ({
  label: BADGE_VERTICAL_ALIGNMENT_LABELS[v],
  value: v,
}))

// Text decoration options
export const BADGE_TEXT_DECORATION_VALUES = ["none", "underline", "strikethrough"] as const
export const BADGE_TEXT_DECORATION_LABELS: Record<
  typeof BADGE_TEXT_DECORATION_VALUES[number],
  string
> = {
  none: "None",
  underline: "Underline",
  strikethrough: "Strikethrough",
}
export const BADGE_TEXT_DECORATION_OPTIONS = BADGE_TEXT_DECORATION_VALUES.map((v) => ({
  label: BADGE_TEXT_DECORATION_LABELS[v],
  value: v,
}))

// Text transform options
export const BADGE_TEXT_TRANSFORM_VALUES = ["none", "capitalize", "uppercase", "lowercase"] as const
export const BADGE_TEXT_TRANSFORM_LABELS: Record<
  typeof BADGE_TEXT_TRANSFORM_VALUES[number],
  string
> = {
  none: "None",
  capitalize: "Capitalize",
  uppercase: "Uppercase",
  lowercase: "Lowercase",
}
export const BADGE_TEXT_TRANSFORM_OPTIONS = BADGE_TEXT_TRANSFORM_VALUES.map((v) => ({
  label: BADGE_TEXT_TRANSFORM_LABELS[v],
  value: v,
}))

// Border style options
export const BADGE_BORDER_STYLE_VALUES = ["solid", "dashed", "dotted"] as const
export const BADGE_BORDER_STYLE_LABELS: Record<typeof BADGE_BORDER_STYLE_VALUES[number], string> = {
  solid: "Solid",
  dashed: "Dashed",
  dotted: "Dotted",
}
export const BADGE_BORDER_STYLE_OPTIONS = BADGE_BORDER_STYLE_VALUES.map((v) => ({
  label: BADGE_BORDER_STYLE_LABELS[v],
  value: v,
}))

// QR Code error correction levels
export const BADGE_QR_ERROR_CORRECTION_VALUES = ["L", "M", "Q", "H"] as const
export const BADGE_QR_ERROR_CORRECTION_LABELS: Record<
  typeof BADGE_QR_ERROR_CORRECTION_VALUES[number],
  string
> = {
  L: "Low (L)",
  M: "Medium (M)",
  Q: "Quartile (Q)",
  H: "High (H)",
}
export const BADGE_QR_ERROR_CORRECTION_OPTIONS = BADGE_QR_ERROR_CORRECTION_VALUES.map((v) => ({
  label: BADGE_QR_ERROR_CORRECTION_LABELS[v],
  value: v,
}))

// Line style options
export const BADGE_LINE_STYLE_VALUES = ["solid", "dashed", "dotted"] as const
export const BADGE_LINE_STYLE_LABELS: Record<typeof BADGE_LINE_STYLE_VALUES[number], string> = {
  solid: "Solid",
  dashed: "Dashed",
  dotted: "Dotted",
}
export const BADGE_LINE_STYLE_OPTIONS = BADGE_LINE_STYLE_VALUES.map((v) => ({
  label: BADGE_LINE_STYLE_LABELS[v],
  value: v,
}))

// Image fit options
export const BADGE_IMAGE_FIT_VALUES = ["cover", "contain", "fill"] as const
export const BADGE_IMAGE_FIT_LABELS: Record<typeof BADGE_IMAGE_FIT_VALUES[number], string> = {
  cover: "Cover",
  contain: "Contain",
  fill: "Fill",
}
export const BADGE_IMAGE_FIT_OPTIONS = BADGE_IMAGE_FIT_VALUES.map((v) => ({
  label: BADGE_IMAGE_FIT_LABELS[v],
  value: v,
}))

// Types
export type BadgeHorizontalAlignment = typeof BADGE_HORIZONTAL_ALIGNMENT_VALUES[number]
export type BadgeVerticalAlignment = typeof BADGE_VERTICAL_ALIGNMENT_VALUES[number]
export type BadgeTextDecoration = typeof BADGE_TEXT_DECORATION_VALUES[number]
export type BadgeTextTransform = typeof BADGE_TEXT_TRANSFORM_VALUES[number]
export type BadgeBorderStyle = typeof BADGE_BORDER_STYLE_VALUES[number]
export type BadgeQRErrorCorrection = typeof BADGE_QR_ERROR_CORRECTION_VALUES[number]
export type BadgeLineStyle = typeof BADGE_LINE_STYLE_VALUES[number]
export type BadgeImageFit = typeof BADGE_IMAGE_FIT_VALUES[number]

// Shapes, lines and images may go past the badge's edge (bleed); everything else stays on it
const BLEED_ELEMENT_TYPES = new Set([
  BadgeElementType.RECTANGLE, BadgeElementType.OVAL, BadgeElementType.LINE, BadgeElementType.IMAGE,
])
export const canLeaveBadge = (type: BadgeElementType): boolean => BLEED_ELEMENT_TYPES.has(type)
